import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { Check, X, ShieldCheck, Sparkles, Zap, ArrowRight, UserCheck, LogIn, AlertCircle } from 'lucide-react';
import { openCashfreeCheckout } from '../../utils/cashfree';
import { InviteAuthModal } from '../../components/InviteAuthModal';
import './InviteUpgradePage.css';

const plansData = {
    '500': {
        invitations: 500,
        price: 299,
        badge: 'Micro Event',
        channels: ['WhatsApp Broadcast Invitation', 'RSVP Tracking & Analytics', 'HTML Email Delivery'],
    },
    '1000': {
        invitations: 1000,
        price: 699,
        badge: 'Most Popular',
        channels: ['WhatsApp Broadcast Invitation', '📸 Photo & Video Sending (WhatsApp)', '⏰ 1-Time Reminder (12 Hrs Before Event)', 'RSVP Tracking & Analytics', 'HTML Email Delivery'],
    },
    '2000': {
        invitations: 2000,
        price: 999,
        badge: 'Grand Wedding',
        channels: ['WhatsApp Broadcast Invitation', '📸 Photo & Video Sending (WhatsApp)', '⏰ 1-Time Reminder (12 Hrs Before Event)', 'RSVP Tracking & Analytics', 'HTML Email Delivery'],
    },
    'ai_plus': {
        invitations: 5000,
        price: 1499,
        badge: '👑 ROYAL VIP & ALL-IN-ONE',
        channels: [
            '🍌 Unlimited Nano Banana Gen AI Copy & Mobile Posts',
            '👑 5,000 Guests Full VIP Broadcast Capacity',
            '🎬 Photo & Video Sending (WhatsApp)',
            '⏰ Multi-Stage Reminders (24h & 12h Before Event)',
            '📊 Real-Time RSVP Dashboard & Dietary Breakdown',
            '⚡ High-Speed Priority Dedicated Delivery Queue',
            'HTML Email & Multi-Channel Delivery',
        ],
    },
};

const InviteUpgradePage: React.FC = () => {
    const { createCashfreeOrder, verifyCashfreePayment, inviteUser, inviteToken } = useInviteAuth();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [selectedPlan, setSelectedPlan] = useState<'500' | '1000' | '2000' | 'ai_plus'>('1000');
    const [upgradeSuccess, setUpgradeSuccess] = useState<any | null>(null);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [paymentNotice, setPaymentNotice] = useState<string | null>(null);

    // Auto-verify if user was redirected back with order_id in URL
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const orderIdParam = queryParams.get('order_id');
        const planParam = (queryParams.get('plan') as '500' | '1000' | '2000' | 'ai_plus') || selectedPlan;

        if (orderIdParam && inviteToken) {
            setLoading(true);
            setLoadingMessage('Confirming transaction with Cashfree servers...');
            verifyCashfreePayment(orderIdParam, planParam)
                .then((verifyResult) => {
                    setUpgradeSuccess(verifyResult);
                    window.history.replaceState({}, document.title, window.location.pathname);
                })
                .catch((err) => {
                    console.warn('[InviteUpgradePage] Auto-verify notice:', err.message);
                    setPaymentNotice(err.message || 'Payment could not be verified.');
                })
                .finally(() => {
                    setLoading(false);
                    setLoadingMessage('');
                });
        }
    }, [inviteToken]);

    const handleCashfreePayment = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setPaymentNotice(null);

        // Check authentication first
        if (!inviteToken || !inviteUser) {
            setIsAuthModalOpen(true);
            return;
        }

        setLoading(true);
        setLoadingMessage('Initializing Cashfree Payment Order...');
        try {
            // 1. Create order on backend via Cashfree PG API
            const orderData = await createCashfreeOrder(selectedPlan);

            // 2. If it's a simulated sandbox order (or test mock session)
            if (orderData.isMock || orderData.paymentSessionId.startsWith('session_mock_') || orderData.paymentSessionId.startsWith('session_sandbox_fallback')) {
                setLoadingMessage('Verifying test payment...');
                const verifyResult = await verifyCashfreePayment(orderData.orderId, selectedPlan);
                setUpgradeSuccess(verifyResult);
                return;
            }

            // 3. Launch official Cashfree JS SDK v3 popup modal
            setLoadingMessage('Opening Cashfree Gateway Popup...');
            const checkoutResult = await openCashfreeCheckout({
                paymentSessionId: orderData.paymentSessionId,
                environment: orderData.environment || 'production',
                redirectTarget: '_modal',
            });

            if (!checkoutResult.success && checkoutResult.error) {
                // If user closed modal or cancelled
                if (checkoutResult.error.toLowerCase().includes('drop') || checkoutResult.error.toLowerCase().includes('close') || checkoutResult.error.toLowerCase().includes('cancel')) {
                    setPaymentNotice('Payment window was closed before completion. You can retry anytime.');
                    return;
                }
                setPaymentNotice(checkoutResult.error);
                return;
            }

            // 4. Verify payment order status with Cashfree server
            setLoadingMessage('Confirming transaction with Cashfree servers...');
            const verifyResult = await verifyCashfreePayment(orderData.orderId, selectedPlan);
            setUpgradeSuccess(verifyResult);
        } catch (err: any) {
            console.error('[InviteUpgradePage] Payment error:', err);
            setPaymentNotice(err.message || 'Payment could not be verified.');
        } finally {
            setLoading(false);
            setLoadingMessage('');
        }
    };

    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    const handleSimulateSandboxPayment = async () => {
        setPaymentNotice(null);
        if (!inviteToken || !inviteUser) {
            setIsAuthModalOpen(true);
            return;
        }

        setLoading(true);
        setLoadingMessage('Simulating Sandbox Instant Verification...');
        try {
            const orderData = await createCashfreeOrder(selectedPlan, true);
            const verifyResult = await verifyCashfreePayment(orderData.orderId, selectedPlan, true);
            setUpgradeSuccess(verifyResult);
        } catch (err: any) {
            setPaymentNotice(`Sandbox Payment failed: ${err.message}`);
        } finally {
            setLoading(false);
            setLoadingMessage('');
        }
    };

    const activePlan = plansData[selectedPlan];

    return (
        <div className="upgrade-page-container">
            {/* Auth Modal for Unauthenticated Users */}
            <InviteAuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onSuccess={() => {
                    setIsAuthModalOpen(false);
                    // Retry payment once logged in
                    handleCashfreePayment();
                }}
            />

            {/* Top Navigation */}
            <div className="upgrade-back-navigation" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button 
                    type="button"
                    onClick={() => history.push('/invite/dashboard')} 
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#A8844A',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.9rem',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        transition: 'all 0.2s',
                        outline: 'none'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(197, 160, 89, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                    }}
                >
                    ← Back to Dashboard
                </button>

                {/* Logged in User Indicator */}
                {inviteUser ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#444', background: '#F8F6F0', padding: '6px 14px', borderRadius: '20px', border: '1px solid rgba(197,160,89,0.2)' }}>
                        <UserCheck size={16} color="#38a169" />
                        <span>Account: <strong>{inviteUser.name}</strong> ({inviteUser.email})</span>
                        {inviteUser.isPremium && (
                            <span style={{ background: '#C5A059', color: '#fff', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
                                {inviteUser.maxInvitations} Limit
                            </span>
                        )}
                    </div>
                ) : (
                    <button
                        onClick={() => setIsAuthModalOpen(true)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'transparent',
                            border: '1.5px solid #C5A059',
                            color: '#A8844A',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        <LogIn size={15} /> Sign In to Upgrade
                    </button>
                )}
            </div>

            <div className="upgrade-page-header">
                <span className="premium-star-badge"><Sparkles size={16} /> Cashfree Unified Payments</span>
                <h2>Choose Your Invitation Package</h2>
                <p>Scale your campaign to match your guest list size. Pay securely via Cashfree (UPI, Cards, NetBanking, QR).</p>
            </div>

            {/* Error / Notice Alert */}
            {paymentNotice && (
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto 1.5rem auto',
                    background: '#FFF5F5',
                    border: '1.5px solid #FEB2B2',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    color: '#C53030',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '0.92rem'
                }}>
                    <AlertCircle size={20} />
                    <span>{paymentNotice}</span>
                </div>
            )}

            {upgradeSuccess ? (
                <div style={{
                    maxWidth: '600px',
                    margin: '2rem auto',
                    background: '#ffffff',
                    borderRadius: '24px',
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    border: '1.5px solid rgba(197, 160, 89, 0.3)',
                    boxShadow: '0 20px 50px rgba(197, 160, 89, 0.15)'
                }}>
                    <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🎉👑</div>
                    <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A', fontSize: '2rem', marginBottom: '0.5rem' }}>
                        Upgrade Completed!
                    </h2>
                    <p style={{ color: '#555', fontSize: '1.05rem', marginBottom: '1.5rem' }}>
                        {upgradeSuccess.message || `Welcome to the ${activePlan.invitations} Guests Premium Package!`}
                    </p>

                    <div style={{
                        background: '#FAF9F6',
                        borderRadius: '16px',
                        padding: '1.25rem',
                        textAlign: 'left',
                        marginBottom: '2rem',
                        border: '1px solid rgba(0,0,0,0.06)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                            <span style={{ color: '#666' }}>Order Reference:</span>
                            <span style={{ fontWeight: 700, color: '#1A1A1A' }}>{upgradeSuccess.transaction?.orderId || 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                            <span style={{ color: '#666' }}>Amount Paid:</span>
                            <span style={{ fontWeight: 700, color: '#A8844A' }}>₹{activePlan.price}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: '#666' }}>Max Invitations Allowed:</span>
                            <span style={{ fontWeight: 700, color: '#38a169' }}>{activePlan.invitations} Guests</span>
                        </div>
                    </div>

                    <button
                        onClick={() => history.push('/invite/dashboard')}
                        style={{
                            padding: '1rem 2.5rem',
                            borderRadius: '30px',
                            background: 'linear-gradient(135deg, #C5A059 0%, #A8844A 100%)',
                            color: '#fff',
                            border: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            cursor: 'pointer',
                            boxShadow: '0 8px 25px rgba(197, 160, 89, 0.35)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        Go to Campaign Dashboard <ArrowRight size={18} />
                    </button>
                </div>
            ) : (
                <div className="upgrade-split-layout">
                    {/* Left Column: Plan Selectors & Features Table */}
                    <div className="upgrade-plans-column">
                        <div className="plans-cards-flex">
                            {(Object.keys(plansData) as Array<'500' | '1000' | '2000' | 'ai_plus'>).map((key) => {
                                const plan = plansData[key];
                                const isSelected = selectedPlan === key;
                                const isCurrent = inviteUser?.isPremium && inviteUser?.maxInvitations === plan.invitations;
                                const isAiPlus = key === 'ai_plus';
                                return (
                                    <div
                                        key={key}
                                        onClick={() => setSelectedPlan(key)}
                                        className={`plan-package-card ${isAiPlus ? 'ai-plus-card' : ''} ${isSelected ? 'selected' : ''} ${isCurrent ? 'current-active' : ''}`}
                                    >
                                        {isCurrent && <span className="current-badge">Current Plan</span>}
                                        {isAiPlus && !isCurrent && <span className="vip-badge">{plan.badge}</span>}
                                        <h3>{isAiPlus ? 'AI+ VIP' : `${plan.invitations} Guests`}</h3>
                                        <div className="plan-price-row">
                                            <span className="currency">₹</span>
                                            <span className="price-num">{plan.price}</span>
                                            <span className="period">/ package</span>
                                        </div>
                                        <p className="plan-summary">
                                            {isAiPlus 
                                                ? '5,000 Guests + Nano Banana AI Unlimited + Media' 
                                                : `Deliver invites up to ${plan.invitations} friends & family.`}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="plan-features-comparison-card">
                            <h3>Plan Deliverables &amp; Channels ({activePlan.invitations} Guests {selectedPlan === 'ai_plus' ? '• Royal VIP' : ''})</h3>
                            <ul className="plan-features-list">
                                {activePlan.channels.map((chan, idx) => (
                                    <li key={idx} className="feature-item">
                                        <span className="feature-check">✓</span>
                                        <span className="feature-text">{chan}</span>
                                    </li>
                                ))}
                                {selectedPlan === '500' && (
                                    <>
                                        <li className="feature-item disabled">
                                            <span className="feature-cross">✗</span>
                                            <span className="feature-text">Photo &amp; Video Sending (WhatsApp)</span>
                                        </li>
                                        <li className="feature-item disabled">
                                            <span className="feature-cross">✗</span>
                                            <span className="feature-text">1-Time Reminder (12 Hrs Before Event)</span>
                                        </li>
                                    </>
                                )}
                                {selectedPlan !== 'ai_plus' && (
                                    <li className="feature-item disabled">
                                        <span className="feature-cross">✗</span>
                                        <span className="feature-text">Nano Banana Gen AI Studio (Included in AI+ Plan for ₹1499)</span>
                                    </li>
                                )}
                                <li className="feature-item divider-top">
                                    <span className="feature-check">✓</span>
                                    <span className="feature-text">Detailed Delivery Analytics & Reports</span>
                                </li>
                                <li className="feature-item">
                                    <span className="feature-check">✓</span>
                                    <span className="feature-text">Excel Recipient Import & RSVP Confirmations</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Right Column: Cashfree Payment Panel */}
                    <div className="upgrade-checkout-column">
                        <div className="checkout-card">
                            <div className="checkout-summary-box">
                                <h4>Order Summary</h4>
                                <div className="summary-item">
                                    <span>Selected Plan</span>
                                    <strong>{activePlan.invitations} Guest Limit</strong>
                                </div>
                                <div className="summary-item">
                                    <span>Payment Gateway</span>
                                    <strong style={{ color: '#A8844A' }}>Cashfree Payments</strong>
                                </div>
                                <div className="summary-item total-row">
                                    <span>Total Amount to Pay</span>
                                    <strong>₹{activePlan.price}</strong>
                                </div>
                            </div>

                            <div style={{
                                background: '#FAF9F6',
                                border: '1px solid rgba(197, 160, 89, 0.2)',
                                borderRadius: '16px',
                                padding: '1.25rem',
                                marginBottom: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#333', marginBottom: '0.5rem' }}>
                                    Supported Payment Methods via Cashfree:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                                    <span style={{ background: '#fff', border: '1px solid #ddd', padding: '4px 8px', borderRadius: '6px' }}>⚡ UPI (GPay, PhonePe, Paytm)</span>
                                    <span style={{ background: '#fff', border: '1px solid #ddd', padding: '4px 8px', borderRadius: '6px' }}>💳 Credit / Debit Cards</span>
                                    <span style={{ background: '#fff', border: '1px solid #ddd', padding: '4px 8px', borderRadius: '6px' }}>🏦 NetBanking (50+ Banks)</span>
                                    <span style={{ background: '#fff', border: '1px solid #ddd', padding: '4px 8px', borderRadius: '6px' }}>📱 QR Code</span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleCashfreePayment()}
                                className="confirm-checkout-btn"
                                disabled={loading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    marginBottom: '0.75rem'
                                }}
                            >
                                <Zap size={18} />
                                {loading ? (loadingMessage || 'Processing...') : `Pay ₹${activePlan.price} with Cashfree`}
                            </button>

                            {/* Sandbox Simulator Action for Rapid Testing (Only visible in Localhost Development) */}
                            {isLocalhost && (
                                <button
                                    type="button"
                                    onClick={handleSimulateSandboxPayment}
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        background: 'transparent',
                                        border: '1.5px dashed rgba(197, 160, 89, 0.5)',
                                        borderRadius: '16px',
                                        color: '#A8844A',
                                        fontSize: '0.82rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        marginBottom: '1rem',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(197, 160, 89, 0.08)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    🧪 Test Mode / Instant Sandbox Upgrade (Demo)
                                </button>
                            )}

                            <p className="checkout-guarantee">
                                <ShieldCheck size={14} /> 256-Bit Encrypted & PCI-DSS Compliant via Cashfree Payments.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InviteUpgradePage;
